import { RefreshTokenContextEnum } from "@/core/domain/enums/seguranca/refresh-token-context.enum";
import { RefreshTokenModalService } from "@/core/services/security/refresh-token-modal.service";
import { SessionService } from "@/core/services/security/session.service";
import {
    formatTokenRemainingTime,
    getJwtRemainingSeconds,
} from "@/core/utils/security/jwt-payload.util";
import { GlobalEvents } from "@/events/global/global.events";
import { inject, Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject } from "rxjs";

@Injectable({
    providedIn: "root",
})
export class SessionTokenLifetimeService implements OnDestroy {
    private sessionService = inject(SessionService);
    private refreshTokenModalService = inject(RefreshTokenModalService);
    private globalEvents = inject(GlobalEvents);

    readonly remainingSeconds$ = new BehaviorSubject<number>(0);

    private intervalId?: ReturnType<typeof setInterval>;
    private expiredHandled = false;

    constructor() {
        this.globalEvents.sessionRestored$.subscribe(() => this.restart());
        this.restart();
    }

    ngOnDestroy(): void {
        this.stop();
    }

    formatRemainingTime(totalSeconds: number): string {
        return formatTokenRemainingTime(totalSeconds);
    }

    shouldShowSessionTimer(): boolean {
        const session = this.sessionService.getInstance();
        return !!session?.isLogged && !!session?.token?.access_token;
    }

    requestReauthentication(): void {
        void this.refreshTokenModalService.open(RefreshTokenContextEnum.TENANT, {
            showToast: false,
            closable: true,
        });
    }

    restart(): void {
        this.stop();
        this.expiredHandled = false;

        const session = this.sessionService.getInstance();
        if (!session?.isLogged || !session?.token?.access_token) {
            this.remainingSeconds$.next(0);
            return;
        }

        this.tick();
        this.intervalId = setInterval(() => this.tick(), 1000);
    }

    private tick(): void {
        const session = this.sessionService.getInstance();
        if (!session?.isLogged || !session?.token?.access_token) {
            this.stop();
            this.remainingSeconds$.next(0);
            return;
        }

        const remaining = getJwtRemainingSeconds(session.token.access_token);
        this.remainingSeconds$.next(remaining);

        if (remaining > 0) {
            this.expiredHandled = false;
            return;
        }

        if (this.expiredHandled || this.refreshTokenModalService.isModalOpen) {
            return;
        }

        this.expiredHandled = true;
        void this.refreshTokenModalService.open(RefreshTokenContextEnum.TENANT, {
            toastKey: "messages.warn.sessionLifetimeExpired",
            toastType: "warn",
        });
    }

    private stop(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = undefined;
        }
    }
}
